/* ==========================================================================
   Google Tag Manager + шар аналітики.

   Дизайн:
   - ID береться з import.meta.env.VITE_GTM_ID (налаштовується без правок коду).
   - Якщо ID порожній — усе працює як no-op (зручно локально / в PR-прев'ю).
   - trackEvent() — єдина точка входу для подій. Пушить у window.dataLayer,
     тож будь-який тег у GTM може на них реагувати.
   - Передбачено хук згоди (consent) — за замовчуванням дозволено; підключи
     свій CMP за потреби.
   ========================================================================== */

const GTM_ID = import.meta.env.VITE_GTM_ID || ''
const DEBUG = String(import.meta.env.VITE_ANALYTICS_DEBUG) === 'true'

let initialized = false
let consentGranted = true // заміни на стан від свого CMP, якщо потрібно

/** Гарантує існування dataLayer і повертає його. */
function getDataLayer() {
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

/** Низькорівневий push у dataLayer (з опційним debug-логом). */
function push(payload) {
  getDataLayer().push(payload)
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.debug('[analytics]', payload)
  }
}

/**
 * Ініціалізує GTM один раз. Безпечно викликати кілька разів.
 * Викликається з main.jsx на старті застосунку.
 */
export function initGTM() {
  if (initialized) return
  if (!GTM_ID) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[analytics] VITE_GTM_ID не задано — GTM вимкнено.')
    }
    return
  }

  // Стандартний снипет GTM, переписаний на модульний JS.
  push({ 'gtm.start': Date.now(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.appendChild(script)

  // <noscript>-фолбек (на випадок вимкненого JS у деяких клієнтів/краулерів).
  const noscript = document.createElement('noscript')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`
  iframe.height = '0'
  iframe.width = '0'
  iframe.style.display = 'none'
  iframe.style.visibility = 'hidden'
  noscript.appendChild(iframe)
  document.body.appendChild(noscript)

  initialized = true
}

/** Керування згодою — виклич із свого банера cookie/CMP. */
export function setConsent(granted) {
  consentGranted = Boolean(granted)
  push({ event: 'consent_update', consent_granted: consentGranted })
}

/**
 * Головний API для подій.
 * @param {string} event - назва події (напр. 'cta_click').
 * @param {object} [params] - довільні параметри події.
 */
export function trackEvent(event, params = {}) {
  if (!event) return
  if (!consentGranted) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.debug('[analytics] подію пропущено (немає згоди):', event)
    }
    return
  }
  push({ event, ...params })
}

/** Зручний шорткат для перегляду «екрана»/секції в SPA. */
export function trackPageView(name, params = {}) {
  trackEvent('page_view', { page_name: name, ...params })
}
